import { useCallback, useState, type Ref } from 'react';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { ChevronDown, Eye } from 'lucide-react';
import {
  Table,
  type SortDirection,
  type TableColumn,
  type TableRow,
} from '~/components/ui/table';
import { RetryBanner } from '~/components/ui/RetryBanner';
import { useFragment as unmaskBuyListItems } from '~/graphql';
import type {
  BuyListItemSummaryFragment,
  MyBuyListsQuery,
  TeamBuyListsQuery,
} from '~/graphql/graphql';
import { buyListItemSummaryFragment } from '~/api/buyList';
import { BuyListLocation } from './BuyListLocation';
import { BuyListStatusBadge } from './BuyListStatusBadge';
import {
  buyerCell,
  buyListNoteTestId,
  expectedShipDateCell,
  useBuyListColumns,
  vehicleCell,
} from './buyListSummaryCells';
import { ProductLengthGrid } from '~/components/common/ProductLengthGrid';

// A column maxWidth styles the header cell only, so the note span must bound itself to clip.
const NOTE_MAX_WIDTH_REM = 20;

export type BuyListSummary =
  | MyBuyListsQuery['myBuyLists']['content'][number]
  | TeamBuyListsQuery['teamBuyLists']['content'][number];

export interface BuyListTableProps {
  buyLists: BuyListSummary[];
  isLoading: boolean;
  isLoadingMore?: boolean;
  sentinelRef?: Ref<HTMLDivElement>;
  hasFailed: boolean;
  loadingMessage: string;
  emptyMessage: string;
  loadFailedMessage: string;
  onRetry: () => void;
  testId: string;
  showBuyerColumn?: boolean;
  onSort?: (key: string, direction: SortDirection) => void;
}

export function BuyListTable({
  buyLists,
  isLoading,
  isLoadingMore = false,
  sentinelRef,
  hasFailed,
  loadingMessage,
  emptyMessage,
  loadFailedMessage,
  onRetry,
  testId,
  showBuyerColumn = false,
  onSort,
}: Readonly<BuyListTableProps>) {
  const navigate = useNavigate();
  const { t } = useTranslation('inventory');
  const summaryColumns = useBuyListColumns({
    showBuyerColumn,
    isSortable: true,
    noteMaxWidth: NOTE_MAX_WIDTH_REM,
  });

  const [collapsedBuyListIds, setCollapsedBuyListIds] = useState<
    ReadonlySet<string>
  >(new Set());
  const toggleBuyList = useCallback(
    (buyListId: string) =>
      setCollapsedBuyListIds((previous) => {
        const next = new Set(previous);
        if (!next.delete(buyListId)) {
          next.add(buyListId);
        }
        return next;
      }),
    []
  );

  const hasRows = buyLists.length > 0;

  const loadFailedBanner = (
    <RetryBanner
      message={loadFailedMessage}
      retryLabel={t('inventory.retry')}
      onRetry={onRetry}
      testId={testId}
    />
  );

  if (isLoading && !hasRows) {
    return (
      <div
        className="p-8 text-center text-neut-sec text-sm"
        data-testid={`${testId}-loading`}
      >
        {loadingMessage}
      </div>
    );
  }

  if (hasFailed && !hasRows) {
    return <div className="mt-6">{loadFailedBanner}</div>;
  }

  const columns: TableColumn[] = [
    ...summaryColumns,
    {
      key: 'status',
      header: t('inventory.buy_list.status.column'),
      width: 8,
    },
    {
      key: 'view',
      header: t('inventory.action'),
      width: 8,
      justification: 'end',
      stickTo: 'right',
    },
  ];

  const rows: TableRow[] = buyLists.flatMap((buyList) => {
    const openBuyList = () => navigate(`/buy-lists/view/${buyList.id}`);
    const items = unmaskBuyListItems(buyListItemSummaryFragment, buyList.items);
    const detailRowId = productsDetailRowId(testId, buyList.id);
    const isExpanded = !collapsedBuyListIds.has(buyList.id);
    const itemCount = (
      <span data-testid={`${testId}-item-count-${buyList.id}`}>
        {buyList.itemCount}
      </span>
    );

    const summaryRow: TableRow = {
      id: { value: buyList.id },
      ...(showBuyerColumn ? { buyer: buyerCell(buyList) } : {}),
      location: {
        value: (
          <button
            type="button"
            className="group flex flex-col items-start gap-0.5 text-left"
            data-testid={`${testId}-open-${buyList.id}`}
            onClick={openBuyList}
          >
            <BuyListLocation buyList={buyList} testId={testId} isLink />
          </button>
        ),
      },
      vehicle: vehicleCell(buyList, testId),
      expectedShipDate: expectedShipDateCell(buyList),
      note: {
        value: buyList.note ? (
          <span
            className="block min-w-0 truncate"
            style={{ maxWidth: `${NOTE_MAX_WIDTH_REM}rem` }}
            title={buyList.note}
            data-testid={buyListNoteTestId(testId, buyList.id)}
          >
            {buyList.note}
          </span>
        ) : (
          ''
        ),
      },
      products: {
        value: (
          <span className="flex items-center gap-1.5">
            {items.length > 0 ? (
              <button
                type="button"
                className="flex items-center gap-1.5 text-neut-prim hover:text-prim transition-colors"
                aria-expanded={isExpanded}
                aria-controls={isExpanded ? detailRowId : undefined}
                aria-label={t('inventory.buy_list.toggle_products', {
                  count: buyList.itemCount,
                })}
                data-testid={`${testId}-products-toggle-${buyList.id}`}
                onClick={() => toggleBuyList(buyList.id)}
              >
                {itemCount}
                <ChevronDown
                  size={16}
                  aria-hidden="true"
                  className={`transition-transform ${isExpanded ? '' : '-rotate-90'}`}
                />
              </button>
            ) : (
              itemCount
            )}
            {buyList.tallyCount > 0 && (
              <>
                <span aria-hidden="true" className="text-neut-sec">
                  ·
                </span>
                <span
                  className="text-xs text-neut-sec"
                  data-testid={`${testId}-tally-count-${buyList.id}`}
                >
                  {t('inventory.buy_list.tallies.count', {
                    count: buyList.tallyCount,
                  })}
                </span>
              </>
            )}
          </span>
        ),
      },
      status: {
        value: (
          <BuyListStatusBadge
            status={buyList.status}
            testId={`${testId}-status-${buyList.id}`}
          />
        ),
      },
      view: {
        value: (
          <button
            type="button"
            className="flex items-center gap-2 px-3 py-1.5 bg-prim text-neut-0 hover:bg-prim-hover text-xs font-medium rounded-lg transition-colors whitespace-nowrap"
            data-testid={`${testId}-view-${buyList.id}`}
            onClick={openBuyList}
          >
            <Eye size={16} aria-hidden="true" />
            {t('inventory.buy_list.view_action')}
          </button>
        ),
      },
    };

    if (!isExpanded || items.length === 0) {
      return [summaryRow];
    }

    return [
      summaryRow,
      buildProductsDetailRow(buyList.id, items, testId, detailRowId),
    ];
  });

  return (
    <div
      className="bg-neut-bg-alt rounded-lg border border-neut-border shadow-sm overflow-hidden"
      data-testid={testId}
    >
      <Table
        minWidth={40}
        rows={rows}
        columns={columns}
        emptyMessage={emptyMessage}
        onSort={onSort}
      />
      {hasFailed ? (
        <div className="p-4">{loadFailedBanner}</div>
      ) : (
        <>
          {isLoadingMore && (
            <div
              className="py-4 text-center text-neut-sec text-sm"
              data-testid={`${testId}-loading-more`}
            >
              {t('inventory.buy_list.loading_more')}
            </div>
          )}
          <div
            ref={sentinelRef}
            className="h-px"
            data-testid={`${testId}-scroll-sentinel`}
          />
        </>
      )}
    </div>
  );
}

function productsDetailRowId(testId: string, buyListId: string): string {
  return `${testId}-products-${buyListId}`;
}

function buildProductsDetailRow(
  buyListId: string,
  items: BuyListItemSummaryFragment[],
  testId: string,
  detailRowId: string
): TableRow {
  return {
    _detail: true,
    _editable: false,
    id: { value: `${buyListId}::products` },
    _testIdSuffix: `products-${buyListId}`,
    _fullWidth: (
      <ProductLengthGrid
        items={items}
        id={detailRowId}
        testId={`${testId}-products-${buyListId}`}
      />
    ),
  };
}
